/**
 * RuntimeQuizConfig — the public, secret-sanitized JSON shape served from
 *   {VITE_QUIZ_CDN_URL}/{VITE_QUIZ_CDN_KEY_PREFIX}/{publisher_id}/{quiz_id}.json
 *
 * Canonical contract for the Offerwall Studio v2 Quiz runtime. Mirrors
 * pga-service's runtime_models.py. When either side changes, this file
 * changes in lockstep — same PR if possible.
 *
 * Producers (must conform to this shape):
 *   - pga-service `editor_to_runtime()` — writes on publish.
 *   - gate-crafter-studio TS port — emits via postMessage for preview.
 *
 * Consumers (validate against this shape):
 *   - this runtime — loads and renders.
 *
 * Cross-repo parity: tests/fixtures/runtime-config-sample.json is the
 * canonical fixture; pga-service mirrors and diffs it (4.2-followup).
 */

export const SUPPORTED_SCHEMA_VERSION = '2026-04-quiz-v1';

// ───────────────────────────────────────────────────────────────────
// Top-level config
// ───────────────────────────────────────────────────────────────────

export interface RuntimeQuizConfig {
  schemaVersion: string;
  publisherId: string;
  quizId: string;
  revision: number;
  status: 'active' | 'inactive';
  locale: string;
  header: RuntimeHeader;
  settings: RuntimeSettings;
  delivery: RuntimeDelivery;
  pgTrigger: RuntimePgTrigger;
  leadDestination: RuntimeLeadDestination;
  footerLinks: RuntimeFooterLink[];
  steps: AnyRuntimeStep[];
}

export interface RuntimeHeader {
  title: string;
  subtitle: string;
  logoUrl: string;
}

export interface RuntimeSettings {
  primaryColor: string;
  backgroundColor: string;
  progressBar: boolean;
  ctaText: string;
  disclaimer: string;
  /** Per-quiz button color. Empty → the runtime falls back to primaryColor. */
  buttonColor: string;
}

export interface RuntimeDelivery {
  url: string;
  // Examples: "top" | "new_tab". Left as string to mirror pga-service's
  // `str` field — tighten when the runtime dispatches on it.
  openMode: string;
}

export interface RuntimePgTrigger {
  enabled: boolean;
  // Examples: "before_delivery". See openMode rationale.
  placement: string;
  disclaimerText: string;
}

/**
 * Always `{mode: "server_side"}` in v2.0 and v2.1.
 *
 * The runtime never sees AC credentials or AC account IDs — pga-service
 * resolves the AC account server-side on lead submit. AC mapping is
 * deliberately kept out of the public config. Typed as a literal so
 * validation refuses any config that drifts off the contract.
 */
export interface RuntimeLeadDestination {
  mode: 'server_side';
}

export interface RuntimeFooterLink {
  label: string;
  url: string;
}

// ───────────────────────────────────────────────────────────────────
// Steps — discriminated union on `type`
// ───────────────────────────────────────────────────────────────────

/**
 * v2.0 supports 5 step types. lead_capture is DEFERRED-V21: pga-service's
 * Pydantic union DOES include RuntimeLeadCaptureStep, but the v2.0
 * runtime deliberately omits the TS variant so validation fails cleanly
 * if a lead_capture step somehow appears in published config. When v2.1
 * lands, add a RuntimeLeadCaptureStep variant here and a corresponding
 * step renderer module under src/steps/lead-capture.ts.
 *
 *   // DEFERRED-V21: | RuntimeLeadCaptureStep
 */
export type AnyRuntimeStep =
  | RuntimeQuestionStep
  | RuntimeImageStep
  | RuntimeSpinWheelStep
  | RuntimeScratchCardStep
  | RuntimeChatStep;

// ── question ──

export interface RuntimeQuestionOption {
  id: string;
  label: string;
}

export interface RuntimeQuestionStep {
  id: string;
  type: 'question';
  index: number;
  title: string;
  subtitle: string;
  options: RuntimeQuestionOption[];
}

// ── image ──

export interface RuntimeImageOption {
  id: string;
  kind: 'image_option';
  label: string;
  imageUrl: string;
}

export interface RuntimeButtonOption {
  id: string;
  kind: 'button_option';
  label: string;
  backgroundColor: string;
  textColor: string;
}

export type AnyRuntimeImageOption = RuntimeImageOption | RuntimeButtonOption;

export interface RuntimeImageStep {
  id: string;
  type: 'image';
  index: number;
  title: string;
  options: AnyRuntimeImageOption[];
}

// ── spin_wheel ──

export interface RuntimeSpinWheelSegment {
  id: string;
  label: string;
  color: string;
  icon: string;
}

export interface RuntimeSpinWheelStep {
  id: string;
  type: 'spin_wheel';
  index: number;
  preSpinTitle: string;
  spinButtonText: string;
  resultButtonText: string;
  segments: RuntimeSpinWheelSegment[];
}

// ── scratch_card ──

export interface RuntimeScratchCardPreOption {
  id: string;
  label: string;
}

export interface RuntimeScratchCardPreQuestion {
  title: string;
  options: RuntimeScratchCardPreOption[];
}

export interface RuntimeScratchCardResultCta {
  label: string;
  url: string;
}

export interface RuntimeScratchCardStep {
  id: string;
  type: 'scratch_card';
  index: number;
  title: string;
  subtitle: string;
  // Pydantic Optional[...] = None serializes as JSON null (not omitted),
  // so this stays `X | null`, not `X?`, under exactOptionalPropertyTypes.
  preQuestion: RuntimeScratchCardPreQuestion | null;
  scratchPrompt: string;
  revealText: string;
  resultCta: RuntimeScratchCardResultCta;
  footerNote: string;
  scratchPercent: number;
  brushWidth: number;
}

// ── chat ──

export interface RuntimeChatQuickReply {
  id: string;
  label: string;
}

export interface RuntimeChatMessage {
  id: string;
  sender: string;
  text: string;
  typingDelay: number;
  quickReplies: RuntimeChatQuickReply[];
}

export interface RuntimeChatBotConfig {
  name: string;
  avatarUrl: string;
  statusText: string;
  headerColor: string;
}

export interface RuntimeChatLastMessageCta {
  label: string;
  url: string;
}

export interface RuntimeChatStep {
  id: string;
  type: 'chat';
  index: number;
  botConfig: RuntimeChatBotConfig;
  messages: RuntimeChatMessage[];
  lastMessageCta: RuntimeChatLastMessageCta | null;
}
